import { computed, ref, type Ref, watch } from 'vue';
import { useToasted } from '~/composables/useToasted';
import debounce from '~/utils/debounce';

export const useBrandOptions = <T extends { value: string }>(
    selectedBrandId: () => string | undefined,
    search: (query?: string) => Promise<T[]>,
    find: (brandId: string) => Promise<T | undefined>
) => {
    const toasted = useToasted();
    const searchResults: Ref<T[]> = ref([]);
    const selectedOption: Ref<T | undefined> = ref();
    const loadingBrands = ref(false);

    // vue-select labels the selection from the options it is given, and a search only
    // returns the first page, so the selected brand is kept in the options on its own.
    const brandOptions = computed(() => {
        const selected = selectedOption.value;

        if (undefined === selected || searchResults.value.some((option) => option.value === selected.value)) {
            return searchResults.value;
        }

        return [selected, ...searchResults.value];
    });

    const searchBrands = debounce((query?: string) => {
        loadingBrands.value = true;

        search(query)
            .then((options) => {
                searchResults.value = options;
            })
            .catch((e) => {
                console.error(e);
                toasted.global.platform_error();
            })
            .finally(() => {
                loadingBrands.value = false;
            });
    }, 300);

    watch(
        selectedBrandId,
        (brandId) => {
            // The campaign form seeds brand_id with '' until a brand is chosen.
            if (undefined === brandId || '' === brandId) {
                selectedOption.value = undefined;

                return;
            }

            const known = brandOptions.value.find((option) => option.value === brandId);

            if (undefined !== known) {
                selectedOption.value = known;

                return;
            }

            find(brandId)
                .then((option) => {
                    selectedOption.value = option;
                })
                .catch((e) => {
                    console.error(e);
                    toasted.global.platform_error();
                });
        },
        { immediate: true }
    );

    return { brandOptions, loadingBrands, searchBrands };
};
